import { Platform } from 'react-native';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { useIsLoggedIn } from './user';
import { analytics } from 'shared/library/analytics';
import { monitoring } from 'shared/library/monitoring';
import { useRoute } from 'shared/library/routes/route';
import { useRouter } from 'shared/library/use-router';

export type LoginStartHint = {
  type: 'modal' | 'page';
  source: string;
};

const useLoginStore = create(
  combine(
    {
      shouldShowLoginModal: false,
      redirectOnSuccess: null as string | null,
    },
    (set) => ({
      setShouldShowLoginModal: (shouldShow: boolean) => set({ shouldShowLoginModal: shouldShow }),
      setRedirectOnSuccess: (redirect: string | null) => set({ redirectOnSuccess: redirect }),
      reset: () => set({ shouldShowLoginModal: false, redirectOnSuccess: null }),
    }),
  ),
);

export const trackLoginStart = (hint: LoginStartHint) => {
  const eventParams = {
    login_type: hint.type,
    login_source: hint.source,
  };
  const sentryEventParams = {
    ...eventParams,
    login_source: monitoring.sentryName(hint.source),
  };

  monitoring.Sentry.addBreadcrumb({
    category: 'auth.login',
    message: 'login_start',
    level: 'info',
    data: sentryEventParams,
  });

  analytics.event('login_start', eventParams);
};

export const useOpenLogin = () => {
  const route = useRoute();
  const router = useRouter();
  const setShouldShowLoginModal = useLoginStore((state) => state.setShouldShowLoginModal);
  const setRedirectOnSuccess = useLoginStore((state) => state.setRedirectOnSuccess);

  return (hint: Omit<LoginStartHint, 'type'>, redirectTo?: string | null) => {
    const loginType: LoginStartHint['type'] = Platform.OS === 'web' ? 'modal' : 'page';

    if (Platform.OS === 'web') {
      trackLoginStart({ type: loginType, source: hint.source });
      setRedirectOnSuccess(redirectTo ?? null);
      setShouldShowLoginModal(true);
      return;
    }

    const loginPath = route('auth.login');

    void router.push(loginPath);
    setTimeout(() => {
      setRedirectOnSuccess(redirectTo ?? null);
      trackLoginStart({ type: loginType, source: hint.source });
    }, 0);
  };
};

export const useShowLoginIfGuest = () => {
  const isLoggedIn = useIsLoggedIn();
  const openLogin = useOpenLogin();

  return (hint: Omit<LoginStartHint, 'type'>, redirectTo?: string) => {
    if (isLoggedIn) {
      return false;
    }

    openLogin(hint, redirectTo);
    return true;
  };
};

export const useShowLoginModalIfGuest = useShowLoginIfGuest;

export default useLoginStore;
